import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Home, Search, AlertTriangle, Shield, ExternalLink, FileText, Building, DollarSign, CheckCircle, Rocket, TrendingUp, Bell } from "lucide-react";
import AgentRequestModal from "../components/AgentRequestModal";
import BuyerSavingsCalculator from "../components/BuyerSavingsCalculator";
import BuyerProtectionBundle from "../components/BuyerProtectionBundle";
import HiddenFSBODeals from "../components/HiddenFSBODeals";

const BuyerResources = ({ user }) => {
  const navigate = useNavigate();
  const [expandedSection, setExpandedSection] = useState(null);
  const [showTaxModal, setShowTaxModal] = useState(false);
  const [showAgentModal, setShowAgentModal] = useState(false);
  const [taxAddress, setTaxAddress] = useState("");
  const [taxCounty, setTaxCounty] = useState("Dallas");

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const openTaxRecords = () => {
    // Dallas County example - would need to customize per county
    const countyUrls = {
      "Dallas": "https://www.dallascad.org/SearchAddr.aspx",
      "Tarrant": "https://www.tad.org/",
      "Collin": "https://www.collincad.org/",
      "Denton": "https://www.dentoncad.com/"
    };
    
    window.open(countyUrls[taxCounty] || countyUrls["Dallas"], '_blank');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-slate-50">
      <Helmet>
        <title>Buyer Resources | Find FSBO Homes in Dallas-Fort Worth | Its-Sold.com</title>
        <meta name="description" content="Find homes for sale by owner in DFW. Save thousands buying direct from homeowners. Free buyer resources, FSBO listings, and home search tools." />
        <link rel="canonical" href="https://its-sold.com/buyer-resources" />
      </Helmet>
      
      {/* Navigation */}
      <nav className="bg-white border-b border-orange-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2 cursor-pointer" onClick={() => navigate('/')}>
              <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center">
                <Home className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Playfair Display' }}>Its-Sold.com<sup className="text-xs" style={{ fontSize: "0.6em", verticalAlign: "super" }}>™</sup></span>
            </div>
            <div className="flex items-center space-x-3">
              <Button onClick={() => navigate('/search')} variant="ghost">Browse Listings</Button>
              {user ? (
                <Button onClick={() => navigate('/dashboard')} className="bg-orange-600 hover:bg-orange-700">
                  Dashboard
                </Button>
              ) : (
                <Button onClick={() => navigate('/')} className="bg-orange-600 hover:bg-orange-700">
                  Sign In
                </Button>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* STICKY HERO BANNER - MASSIVE CONVERSION DRIVER */}
      <div className="sticky top-0 z-40 bg-gradient-to-r from-blue-600 via-blue-700 to-blue-800 text-white py-6 shadow-2xl border-b-4 border-yellow-400">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="text-center md:text-left">
              <h2 className="text-3xl md:text-4xl font-extrabold mb-2">
                💰 Save $15,000–$30,000 Buying Direct from DFW Homeowners
              </h2>
              <p className="text-lg text-blue-100">
                No Agent Fees Passed to You · Browse Active FSBO Homes · New Listings Daily
              </p>
            </div>
            <Button
              onClick={() => navigate('/search')}
              className="bg-gradient-to-r from-orange-500 to-yellow-400 hover:from-orange-600 hover:to-yellow-500 text-gray-900 font-extrabold text-xl md:text-2xl py-6 md:py-8 px-8 md:px-16 rounded-xl shadow-2xl transform hover:scale-105 transition-all whitespace-nowrap"
            >
              <Rocket className="w-6 h-6 mr-2" />
              Show Me FSBO Homes Now →
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Playfair Display' }}>
            FSBO Buyer Resources
          </h1>
          <p className="text-xl text-gray-700 mb-3">
            Save Big & Buy Smart When Purchasing For Sale By Owner
          </p>
          <p className="text-base text-gray-600 max-w-3xl mx-auto">
            Buying FSBO saves you money! We&apos;ll show you exactly what to check and the professionals you need.
          </p>
        </div>

        {/* HIDDEN FSBO DEALS CAROUSEL - FOMO DRIVER */}
        <div className="mb-12">
          <HiddenFSBODeals onJoinClick={() => navigate('/register')} />
        </div>

        {/* BUYER SAVINGS CALCULATOR - VIRAL POTENTIAL */}
        <div className="mb-12">
          <BuyerSavingsCalculator />
        </div>

        {/* Quick Tax Record Lookup */}
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-2xl p-8 mb-12 text-white">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-center mb-4">
              <Search className="w-8 h-8 mr-3" />
              <h2 className="text-3xl font-bold" style={{ fontFamily: 'Playfair Display' }}>
                Quick Property Tax Record Lookup
              </h2>
            </div>
            <p className="text-center text-lg mb-6">
              Check property tax history, assessed value, and ownership information
            </p>
            <div className="bg-white rounded-xl p-6 max-w-2xl mx-auto">
              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Property Address</label>
                  <Input
                    placeholder="123 Main St, Dallas, TX"
                    value={taxAddress}
                    onChange={(e) => setTaxAddress(e.target.value)}
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">County</label>
                  <select
                    value={taxCounty}
                    onChange={(e) => setTaxCounty(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="Dallas">Dallas County</option>
                    <option value="Tarrant">Tarrant County</option>
                    <option value="Collin">Collin County</option>
                    <option value="Denton">Denton County</option>
                    <option value="Rockwall">Rockwall County</option>
                  </select>
                </div>
              </div>
              <Button
                onClick={openTaxRecords}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
              >
                <Search className="w-4 h-4 mr-2" />
                View Tax Records
              </Button>
            </div>
          </div>
        </div>

        {/* Essential Due Diligence */}
        <div className="space-y-6">
          {/* Title Search & Lien Check */}
          <Card className="p-6 border-2 border-red-200">
            <div 
              className="flex items-start justify-between cursor-pointer"
              onClick={() => toggleSection('liens')}
            >
              <div className="flex items-start space-x-4 flex-1">
                <div className="w-14 h-14 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="w-8 h-8 text-red-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">⚠️ Check for Liens & Title Issues</h3>
                  <p className="text-gray-600 mb-2">
                    <strong>CRITICAL:</strong> The property could have liens, tax debts, or ownership disputes
                  </p>
                  <p className="text-sm text-gray-500">
                    Tax liens • Mechanics liens • Mortgage liens • Judgment liens • Title defects
                  </p>
                </div>
              </div>
              <Button variant="ghost" size="sm">
                {expandedSection === 'liens' ? '−' : '+'}
              </Button>
            </div>
            
            {expandedSection === 'liens' && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="bg-red-50 border-2 border-red-300 rounded-lg p-5 mb-6">
                  <div className="flex items-start space-x-3">
                    <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h5 className="font-bold text-red-900 mb-2">🚨 DO NOT SKIP THIS STEP</h5>
                      <p className="text-sm text-red-800 mb-2">
                        If you buy a property with a lien, <strong>YOU become responsible for that debt</strong>. 
                        A $300,000 home could have a $50,000 tax lien you don't discover until after closing.
                      </p>
                      <p className="text-sm text-red-800 font-semibold">
                        Real estate agents ALWAYS order title searches before closing. This is NON-NEGOTIABLE for buyers.
                      </p>
                    </div>
                  </div>
                </div>

                <h4 className="font-bold text-gray-900 mb-4">What Can Be Wrong With a Title?</h4>
                <div className="grid md:grid-cols-2 gap-4 mb-6">
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <h5 className="font-bold text-gray-900 mb-2 flex items-center">
                      <DollarSign className="w-5 h-5 text-red-600 mr-2" />
                      Tax Liens
                    </h5>
                    <p className="text-sm text-gray-600">
                      Unpaid property taxes become a lien against the property. The buyer inherits this debt unless paid at closing.
                    </p>
                  </div>

                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <h5 className="font-bold text-gray-900 mb-2 flex items-center">
                      <Building className="w-5 h-5 text-red-600 mr-2" />
                      Mechanics Liens
                    </h5>
                    <p className="text-sm text-gray-600">
                      If the seller didn't pay contractors for work done, those contractors can place a lien on the property.
                    </p>
                  </div>

                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <h5 className="font-bold text-gray-900 mb-2 flex items-center">
                      <Shield className="w-5 h-5 text-red-600 mr-2" />
                      Second Mortgages
                    </h5>
                    <p className="text-sm text-gray-600">
                      The seller may have a second mortgage or HELOC that must be paid off at closing.
                    </p>
                  </div>

                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <h5 className="font-bold text-gray-900 mb-2 flex items-center">
                      <FileText className="w-5 h-5 text-red-600 mr-2" />
                      Judgment Liens
                    </h5>
                    <p className="text-sm text-gray-600">
                      Court judgments against the owner (lawsuits, credit card debt) can become liens on the property.
                    </p>
                  </div>
                </div>

                <h4 className="font-bold text-gray-900 mb-4">How to Protect Yourself</h4>
                <div className="space-y-4">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <h5 className="font-bold text-green-900 mb-3 text-lg">✅ REQUIRED: Title Company Search</h5>
                    <p className="text-sm text-gray-700 mb-4">
                      Hire a title company to perform a complete title search. This is typically $300-500 and is 
                      <strong> the ONLY way</strong> to find all liens and title issues.
                    </p>
                    
                    <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
                      <h6 className="font-semibold text-gray-900 mb-2">Recommended Title Companies:</h6>
                      <ul className="space-y-2 text-sm text-gray-700">
                        <li className="flex items-start">
                          <CheckCircle className="w-4 h-4 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                          <div>
                            <strong>Stewart Title:</strong> 
                            <a href="https://www.stewart.com/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline ml-1">
                              www.stewart.com <ExternalLink className="w-3 h-3 inline" />
                            </a>
                          </div>
                        </li>
                        <li className="flex items-start">
                          <CheckCircle className="w-4 h-4 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                          <div>
                            <strong>First American Title:</strong> 
                            <a href="https://www.firstam.com/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline ml-1">
                              www.firstam.com <ExternalLink className="w-3 h-3 inline" />
                            </a>
                          </div>
                        </li>
                        <li className="flex items-start">
                          <CheckCircle className="w-4 h-4 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                          <div>
                            <strong>Chicago Title:</strong> 
                            <a href="https://www.ctt.com/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline ml-1">
                              www.ctt.com <ExternalLink className="w-3 h-3 inline" />
                            </a>
                          </div>
                        </li>
                      </ul>
                    </div>

                    <p className="text-sm text-gray-700">
                      The title company will search public records for all liens, judgments, and ownership issues. 
                      They'll provide title insurance that protects you if a lien is missed.
                    </p>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h5 className="font-bold text-blue-900 mb-2">What Real Estate Agents Do</h5>
                    <p className="text-sm text-blue-800">
                      Agents automatically order title searches as part of every transaction. They review the title commitment, 
                      identify issues, and ensure all liens are paid at closing. They work with title companies daily and know 
                      what red flags to look for. This protection is a major reason why buyers are traditionally represented by agents.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </Card>

          {/* Property Inspection */}
          <Card className="p-6 border-2 border-yellow-200">
            <div 
              className="flex items-start justify-between cursor-pointer"
              onClick={() => toggleSection('inspection')}
            >
              <div className="flex items-start space-x-4 flex-1">
                <div className="w-14 h-14 bg-yellow-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Search className="w-8 h-8 text-yellow-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Professional Home Inspection</h3>
                  <p className="text-gray-600 mb-2">
                    <strong>What Agents Do:</strong> Recommend inspectors, attend inspections, negotiate repairs
                  </p>
                  <p className="text-sm text-gray-500">
                    Structural issues • System failures • Safety hazards • Repair negotiations
                  </p>
                </div>
              </div>
              <Button variant="ghost" size="sm">
                {expandedSection === 'inspection' ? '−' : '+'}
              </Button>
            </div>
            
            {expandedSection === 'inspection' && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                  <p className="text-sm text-amber-900">
                    <strong>Never Waive Inspection:</strong> Even if the seller's disclosure says everything is fine, 
                    hire a professional home inspector ($400-600). They find issues the seller may not know about or disclose.
                  </p>
                </div>

                <p className="text-sm text-gray-600 mb-4">
                  Agents have trusted home inspectors they work with regularly. They attend the inspection, review the report, 
                  and help you decide what repairs to request. Without an agent, you're on your own to interpret a 50-page 
                  inspection report and negotiate repairs.
                </p>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="text-sm text-yellow-900">
                    <strong>Agent Value:</strong> Experienced agents know which inspection issues are deal-breakers vs. minor concerns. 
                    They've seen thousands of inspection reports and can advise you on what's normal wear vs. serious problems.
                  </p>
                </div>
              </div>
            )}
          </Card>

          {/* Appraisal */}
          <Card className="p-6 border-2 border-purple-200">
            <div 
              className="flex items-start justify-between cursor-pointer"
              onClick={() => toggleSection('appraisal')}
            >
              <div className="flex items-start space-x-4 flex-1">
                <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <DollarSign className="w-8 h-8 text-purple-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Property Appraisal</h3>
                  <p className="text-gray-600 mb-2">
                    <strong>What Agents Do:</strong> Prepare for appraisal, provide comps, handle low appraisal negotiations
                  </p>
                  <p className="text-sm text-gray-500">
                    Fair market value • Lender requirement • Price negotiations
                  </p>
                </div>
              </div>
              <Button variant="ghost" size="sm">
                {expandedSection === 'appraisal' ? '−' : '+'}
              </Button>
            </div>
            
            {expandedSection === 'appraisal' && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <p className="text-sm text-gray-600 mb-4">
                  If you're financing, your lender will require an appraisal ($500-700). If the appraisal comes in lower 
                  than the purchase price, you'll need to renegotiate or bring extra cash.
                </p>

                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <p className="text-sm text-purple-900">
                    <strong>Agent Value:</strong> Agents prepare for appraisals by providing comparable sales data. 
                    When appraisals come in low (happens in 10-15% of sales), agents negotiate with sellers to reduce 
                    the price or find creative solutions to keep deals together.
                  </p>
                </div>
              </div>
            )}
          </Card>
        </div>

        {/* Free Valuation Tool CTA for Buyers */}
        <div className="mt-16 bg-gradient-to-r from-blue-500 to-blue-600 rounded-2xl p-12 text-center text-white">
          <h2 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Playfair Display' }}>
            Don't Pay Too Much! 💰
          </h2>
          <p className="text-lg mb-6 max-w-3xl mx-auto">
            Before you make an offer, check recent sales in your target area. Get FREE instant home valuations, 
            access tax records, and see what similar homes are selling for in the Dallas-Fort Worth market.
          </p>
          <Button
            onClick={() => navigate('/home-valuation')}
            className="bg-white text-blue-600 hover:bg-gray-100 text-lg px-8 py-6 mb-4"
          >
            <DollarSign className="w-5 h-5 mr-2" />
            Check Market Values - Free Tool
          </Button>
          <p className="text-sm text-blue-100">
            Know the market • Negotiate confidently • Pay fair prices
          </p>
        </div>

        {/* DFW Lifestyle & Amenities */}
        <div className="mt-16 bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl p-8 sm:p-12">
          <div className="text-center mb-8">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3" style={{ fontFamily: 'Playfair Display' }}>
              Living in the Dallas-Fort Worth Metroplex
            </h2>
            <p className="text-base sm:text-lg text-gray-700 max-w-3xl mx-auto">
              More than just a home - discover the vibrant lifestyle, world-class amenities, and endless opportunities that make DFW one of America's most desirable places to live.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Sports & Entertainment */}
            <Card className="p-6 hover:shadow-lg transition-shadow">
              <div className="text-center mb-4">
                <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-3xl">🏈</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">World-Class Sports</h3>
              </div>
              <ul className="space-y-2 text-sm text-gray-700">
                <li>• <strong>Dallas Cowboys</strong> - AT&T Stadium</li>
                <li>• <strong>Dallas Mavericks</strong> - American Airlines Center</li>
                <li>• <strong>Texas Rangers</strong> - Globe Life Field</li>
                <li>• <strong>Dallas Stars</strong> (NHL) - Hockey Excellence</li>
                <li>• <strong>FC Dallas</strong> - Major League Soccer</li>
                <li>• <strong>Dallas Wings</strong> (WNBA)</li>
              </ul>
            </Card>

            {/* Golf & Recreation */}
            <Card className="p-6 hover:shadow-lg transition-shadow">
              <div className="text-center mb-4">
                <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-3xl">⛳</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">200+ Golf Courses</h3>
              </div>
              <ul className="space-y-2 text-sm text-gray-700">
                <li>• <strong>TPC Four Seasons</strong> - Byron Nelson Championship</li>
                <li>• <strong>Cowboys Golf Club</strong> - PGA Tour Design</li>
                <li>• <strong>Trinity Forest</strong> - Links-Style Course</li>
                <li>• <strong>Top Golf</strong> - Multiple Locations</li>
                <li>• <strong>White Rock Lake</strong> - Biking, Running, Sailing</li>
                <li>• <strong>Trinity Trail System</strong> - 70+ Miles</li>
              </ul>
            </Card>

            {/* Arts & Culture */}
            <Card className="p-6 hover:shadow-lg transition-shadow">
              <div className="text-center mb-4">
                <div className="w-14 h-14 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-3xl">🎭</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Arts & Culture</h3>
              </div>
              <ul className="space-y-2 text-sm text-gray-700">
                <li>• <strong>Dallas Arts District</strong> - Largest Urban Arts District</li>
                <li>• <strong>Perot Museum</strong> - Science & Nature</li>
                <li>• <strong>Dallas Museum of Art</strong> - 24,000+ Works</li>
                <li>• <strong>Fort Worth Stockyards</strong> - Historic Western Culture</li>
                <li>• <strong>Kimbell Art Museum</strong> - World-Renowned</li>
                <li>• <strong>AT&T Performing Arts</strong> - Broadway Shows</li>
              </ul>
            </Card>

            {/* Family Entertainment */}
            <Card className="p-6 hover:shadow-lg transition-shadow">
              <div className="text-center mb-4">
                <div className="w-14 h-14 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-3xl">🎢</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Family Fun</h3>
              </div>
              <ul className="space-y-2 text-sm text-gray-700">
                <li>• <strong>Six Flags Over Texas</strong> - Thrill Rides</li>
                <li>• <strong>Hurricane Harbor</strong> - Water Park</li>
                <li>• <strong>Fort Worth Zoo</strong> - Top 5 in U.S.</li>
                <li>• <strong>Dallas World Aquarium</strong></li>
                <li>• <strong>Zero Gravity</strong> - Trampoline Park</li>
                <li>• <strong>Legoland Discovery</strong> - Interactive Fun</li>
              </ul>
            </Card>

            {/* Dining & Nightlife */}
            <Card className="p-6 hover:shadow-lg transition-shadow">
              <div className="text-center mb-4">
                <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-3xl">🍴</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Dining & Nightlife</h3>
              </div>
              <ul className="space-y-2 text-sm text-gray-700">
                <li>• <strong>Deep Ellum</strong> - Live Music & Nightlife</li>
                <li>• <strong>Bishop Arts District</strong> - Trendy Restaurants</li>
                <li>• <strong>Uptown Dallas</strong> - Upscale Dining</li>
                <li>• <strong>Sundance Square</strong> - Fort Worth Entertainment</li>
                <li>• <strong>The Cedars</strong> - Craft Breweries</li>
                <li>• <strong>5,000+ Restaurants</strong> - Every Cuisine</li>
              </ul>
            </Card>

            {/* Shopping & Lifestyle */}
            <Card className="p-6 hover:shadow-lg transition-shadow">
              <div className="text-center mb-4">
                <div className="w-14 h-14 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-3xl">🛍️</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Shopping Paradise</h3>
              </div>
              <ul className="space-y-2 text-sm text-gray-700">
                <li>• <strong>NorthPark Center</strong> - Luxury Shopping</li>
                <li>• <strong>Galleria Dallas</strong> - 200+ Stores</li>
                <li>• <strong>Legacy West</strong> - Plano's Lifestyle Center</li>
                <li>• <strong>Shops at Willow Bend</strong></li>
                <li>• <strong>Stonebriar Centre</strong> - Family Shopping</li>
                <li>• <strong>Farmers Markets</strong> - Fresh & Local</li>
              </ul>
            </Card>
          </div>

          {/* Quick Stats */}
          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <div className="bg-white rounded-lg p-4">
              <div className="text-2xl sm:text-3xl font-bold text-orange-600">300+</div>
              <div className="text-xs sm:text-sm text-gray-600">Sunny Days/Year</div>
            </div>
            <div className="bg-white rounded-lg p-4">
              <div className="text-2xl sm:text-3xl font-bold text-orange-600">#1</div>
              <div className="text-xs sm:text-sm text-gray-600">Job Growth in U.S.</div>
            </div>
            <div className="bg-white rounded-lg p-4">
              <div className="text-2xl sm:text-3xl font-bold text-orange-600">7.6M</div>
              <div className="text-xs sm:text-sm text-gray-600">Metro Population</div>
            </div>
            <div className="bg-white rounded-lg p-4">
              <div className="text-2xl sm:text-3xl font-bold text-orange-600">No</div>
              <div className="text-xs sm:text-sm text-gray-600">State Income Tax</div>
            </div>
          </div>

          <div className="mt-8 text-center">
            <p className="text-sm text-gray-600 italic">
              DFW combines big-city opportunities with Texas hospitality, making it one of the fastest-growing metros in America.
            </p>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 bg-gradient-to-br from-blue-100 to-cyan-100 rounded-2xl p-12 text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Playfair Display' }}>
            Ready to Work With a Professional?
          </h2>
          <p className="text-lg text-gray-700 mb-6 max-w-3xl mx-auto">
            As you can see, there's a lot to manage when buying a home. Buyer's agents coordinate all these services, 
            protect your interests in negotiations, and guide you through the process - usually at no direct cost to you 
            (seller pays the commission). 
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Button 
              onClick={() => navigate('/search')}
              className="bg-blue-600 hover:bg-blue-700 text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6"
            >
              Continue Searching FSBO
            </Button>
            <Button 
              onClick={() => setShowAgentModal(true)}
              variant="outline"
              className="border-2 border-blue-600 text-blue-600 hover:bg-blue-50 text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6"
            >
              Find a Buyer's Agent
            </Button>
          </div>
          <p className="text-xs text-gray-500 mt-6 max-w-2xl mx-auto italic">
            <strong>Disclaimer:</strong> Its-Sold.com™ is an independent FSBO platform. We are not affiliated with, sponsored by, or representing any real estate brokerage firm. Agent referrals are provided as a free service to help buyers and sellers connect with licensed professionals.
          </p>
        </div>
      </div>

      {/* Agent Request Modal */}}
      <AgentRequestModal 
        isOpen={showAgentModal} 
        onClose={() => setShowAgentModal(false)} 
      />
    </div>
  );
};

export default BuyerResources;
